// tables/SalesReturnTable.tsx
import React from 'react';
import { Card } from '@components/card/card';

interface SalesReturnTableProps {
  data: any[];
  selectedId: string | null;
  onSelect: (id: string, code: string) => void;
  isLoading: boolean;
  language: string;
}

export const SalesReturnTable: React.FC<SalesReturnTableProps> = ({
  data,
  selectedId,
  onSelect,
  isLoading,
  language
}) => {
  const isArabic = language === 'ar';

  const getTotalAmount = () => {
    return data.reduce((sum, item) => sum + (item.netSale || item.grossSale || 0), 0);
  };

  const getSelectedAmount = () => {
    const selectedItem = data.find(item => item.id === selectedId);
    return selectedItem ? (selectedItem.netSale || selectedItem.grossSale || 0) : 0;
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
        <p className="text-muted mt-2">جاري تحميل المرتجعات...</p>
      </div>
    );
  }

  return (
    <Card className="mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <i className="fas fa-undo-alt me-2"></i>
          قائمة مرتجعات المبيعات
          <span className="badge bg-danger ms-2">مرتجعات مبيعات</span>
        </h6>
        <div>
          <span className="badge bg-theme me-2">
            {selectedId ? 'تم الاختيار' : 'لم يتم الاختيار'}
          </span>
          <span className="badge bg-success">
            الإجمالي: {getTotalAmount().toLocaleString()} ر.س
          </span>
        </div>
      </div>

      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light fw-bold">
              <tr>
                <th className="text-center small" style={{ width: '50px' }}>#</th>
                <th className="text-center small" style={{ width: '15%' }}>رقم المرتجع</th>
                <th className="text-center small" style={{ width: '15%' }}>التاريخ</th>
                <th className="text-center small" style={{ width: '20%' }}>المستلم</th>
                <th className="text-center small" style={{ width: '15%' }}>الإجمالي</th>
                <th className="text-center small" style={{ width: '15%' }}>الضريبة</th>
                <th className="text-center small" style={{ width: '20%' }}>الصافي</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => {
                const isSelected = selectedId === item.id;
                
                return (
                  <tr 
                    key={item.id} 
                    className={isSelected ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSelect(item.id, item.code)}
                  >
                    <td className="text-center align-middle">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          name="salesReturnSelection"
                          checked={isSelected}
                          onChange={() => onSelect(item.id, item.code)}
                        />
                      </div>
                    </td>
                    
                    <td className="text-center align-middle fw-bold">
                      {item.code}
                    </td>
                    
                    <td className="text-center align-middle">
                      {item.transactionDate 
                        ? new Date(item.transactionDate).toLocaleDateString('ar-SA')
                        : '-'}
                    </td>
                    
                    <td className="text-center align-middle">
                      {item.recipientname || '-'}
                    </td>
                    
                    <td className="text-center align-middle">
                      <span className="fw-bold text-warning">
                        {(item.grossSale || 0).toLocaleString()} ر.س
                      </span>
                    </td>
                    
                    <td className="text-center align-middle">
                      <span className="fw-bold text-info">
                        {(item.vat || 0).toLocaleString()} ر.س
                      </span>
                    </td>
                    
                    <td className="text-center align-middle">
                      <span className="fw-bold text-success">
                        {(item.netSale || 0).toLocaleString()} ر.س
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-footer d-flex justify-content-between align-items-center">
        <div className="text-muted small">
          عرض {data.length} من {data.length} مرتجع مبيعات
        </div>
        <div className="text-theme fw-bold">
          {selectedId 
            ? `المبلغ المحدد: ${getSelectedAmount().toLocaleString()} ر.س`
            : 'يرجى اختيار مرتجع'}
        </div>
      </div>
    </Card>
  );
};